import type { CreateServiceRequest, Service } from "@/types/api";
import type { LlmProvider } from "./LlmFields";
import { useGeneratorFields } from "./useGeneratorFields";
import { useHttpFields } from "./useHttpFields";
import { useHttpSourceFields } from "./useHttpSourceFields";
import { useKafkaFields } from "./useKafkaFields";
import { useLlmFields } from "./useLlmFields";
import { useMongoFields } from "./useMongoFields";
import { usePostgresCdcFields } from "./usePostgresCdcFields";
import { usePostgresFields } from "./usePostgresFields";
import { usePubSubFields } from "./usePubSubFields";
import { useScannerFields } from "./useScannerFields";
import { useUdfFields } from "./useUdfFields";

export type Provider = Service["provider"];

// What the Provider picker offers: the concrete providers, with every LLM
// folded into one "llm" category whose card picks the AI provider.
export type ProviderChoice = Exclude<Provider, LlmProvider> | "llm";

export type ProviderFields = {
  fields: JSX.Element;
  toPayload: () => Partial<CreateServiceRequest>;
};

export const PROVIDER_OPTIONS: { value: ProviderChoice; label: string }[] = [
  { value: "mongodb", label: "MongoDB" },
  { value: "postgres", label: "PostgreSQL" },
  { value: "postgres_cdc", label: "PostgreSQL CDC (change capture)" },
  { value: "kafka", label: "Kafka" },
  { value: "pubsub", label: "Google Pub/Sub" },
  { value: "http", label: "HTTP Client" },
  { value: "http_source", label: "HTTP Source" },
  { value: "udf", label: "UDF (User Defined Function)" },
  { value: "generator", label: "Generator (synthetic data)" },
  { value: "scanner", label: "Scanner (read existing data)" },
  { value: "llm", label: "LLM" },
];

// Every provider hook runs on each render (hooks can't be conditional); only
// the selected provider's fields end up rendered. The service name feeds the
// UDF hook's derived script-path default. The LLM category resolves to the
// AI provider chosen inside its card, and may act once the service exists
// (setting it as the default model).
export function useProviderFields(
  choice: ProviderChoice,
  serviceName = "",
): ProviderFields & {
  provider: Provider;
  afterCreate?: (name: string) => Promise<void>;
} {
  const llm = useLlmFields();
  const hooks: Record<Exclude<ProviderChoice, "llm">, ProviderFields> = {
    mongodb: useMongoFields(),
    postgres: usePostgresFields(),
    postgres_cdc: usePostgresCdcFields(serviceName),
    kafka: useKafkaFields(),
    pubsub: usePubSubFields(),
    http: useHttpFields(),
    http_source: useHttpSourceFields(),
    udf: useUdfFields(serviceName),
    generator: useGeneratorFields(),
    scanner: useScannerFields(),
  };
  if (choice === "llm") return llm;
  return { ...hooks[choice], provider: choice };
}
